import { camelCase, findLast, upperFirst } from 'lodash';
import { MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  StyledH2,
  StyledNarrowWrapper,
  StyledText,
} from '../../../pages/UploadDocuments/UploadDocuments.styles';
import {
  DOCUMENTS_SIDES,
  GUARANTOR_DOCS_DESCRIPTIONS,
  UPLOAD_DOCUMENTS_FLOW_STEPS,
  USERS_TYPES,
} from '../../../shared/constants';
import { areGuarantorDocumentsUploaded } from '../../../shared/helpers';
import useGuarantorDocuments from '../../../shared/hooks/useGuarantorDocuments';
import useStore from '../../../store';
import DocumentField from '../../DocumentField';
import UploadDocumentsActionBar from '../ActionBar';
import { StyledNarrowerContainer } from './UploadGuarantorDocuments.styles';

const UploadGuarantorDocumentsSteps = (): JSX.Element => {
  const { t } = useTranslation(['common', 'documentsflow', 'form']);

  const { state } = useLocation() as {
    state: { isUserTheGuarantor: boolean };
  };
  const navigate = useNavigate();
  const { locale } = useParams();
  const [searchParams] = useSearchParams();

  const bookingId = searchParams.get('booking_id');
  const guarantorId = Number(searchParams.get('guarantor_id'));

  const guarantors = useStore(state => state.guarantors);
  const guarantor = guarantors.find(g => g.id === guarantorId);
  const guarantorOccupation = camelCase(guarantor?.occupation);
  const guarantorDocuments = guarantor?.documents;

  const isLoadingGuarantorDocuments = useGuarantorDocuments(guarantorId);

  const ref = useRef(null) as MutableRefObject<HTMLDivElement | null>;

  const identityDocument = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'identity_document',
      })
    : undefined;
  const taxNumber = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'tax_number',
      })
    : undefined;
  const thirdLastPaycheck = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.THIRD_LAST_PAYCHECK,
      })
    : undefined;
  const secondLastPaycheck = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_PAYCHECK,
      })
    : undefined;
  const lastPaycheck = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.LAST_PAYCHECK,
      })
    : undefined;
  const workContract = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.WORK_CONTRACT,
      })
    : undefined;
  const secondLastIncomeTaxReturn = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_INCOME_TAX_RETURN,
      })
    : undefined;
  const lastIncomeTaxReturn = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.LAST_INCOME_TAX_RETURN,
      })
    : undefined;
  const banckAccountBalance = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.BANK_ACCOUNT_BALANCE,
      })
    : undefined;
  const universityCertification = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'other',
        description: GUARANTOR_DOCS_DESCRIPTIONS.UNIVERSITY_CERTIFICATION,
      })
    : undefined;

  const areAllDocumentsUploaded = guarantorDocuments
    ? areGuarantorDocumentsUploaded(guarantor!, state.isUserTheGuarantor)
    : false;

  return (
    <>
      {!isLoadingGuarantorDocuments && (
        <>
          <StyledNarrowWrapper>
            <StyledH2>
              {upperFirst(
                state.isUserTheGuarantor
                  ? t('documentsflow:uploadYourIncomeDocuments')
                  : `${t('documentsflow:uploadIncomeDocumentsOf')} ${upperFirst(
                      guarantor?.first_name,
                    )} ${upperFirst(guarantor?.last_name)}`,
              )}
            </StyledH2>
            <StyledText black>
              {upperFirst(t('documentsflow:documentsMaxSize'))}
            </StyledText>
            <StyledNarrowerContainer ref={ref}>
              {(guarantor?.occupation === USERS_TYPES.FOREIGN_STUDENT ||
                guarantor?.occupation === USERS_TYPES.ITALIAN_STUDENT) && (
                <DocumentField
                  documentSide={DOCUMENTS_SIDES.FRONT}
                  title={upperFirst(t('form:universityCertification'))}
                  type="other"
                  documentUploaded={universityCertification}
                  ownerId={guarantorId}
                  isGuarantor
                  description={
                    GUARANTOR_DOCS_DESCRIPTIONS.UNIVERSITY_CERTIFICATION
                  }
                />
              )}
              {(guarantorOccupation === USERS_TYPES.FOREIGN_STUDENT ||
                guarantorOccupation === USERS_TYPES.SELF_EMPLOYED) && (
                <DocumentField
                  documentSide={DOCUMENTS_SIDES.FRONT}
                  title={upperFirst(t('form:bankAccountBalance'))}
                  type="proof_of_income"
                  documentUploaded={banckAccountBalance}
                  ownerId={guarantorId}
                  isGuarantor
                  description={GUARANTOR_DOCS_DESCRIPTIONS.BANK_ACCOUNT_BALANCE}
                />
              )}
              {guarantorOccupation === USERS_TYPES.SELF_EMPLOYED && (
                <>
                  <DocumentField
                    documentSide={DOCUMENTS_SIDES.FRONT}
                    title={upperFirst(t('form:lastIncomeTaxReturn'))}
                    type="proof_of_income"
                    documentUploaded={lastIncomeTaxReturn}
                    ownerId={guarantorId}
                    isGuarantor
                    description={
                      GUARANTOR_DOCS_DESCRIPTIONS.LAST_INCOME_TAX_RETURN
                    }
                  />
                  <DocumentField
                    documentSide={DOCUMENTS_SIDES.FRONT}
                    title={upperFirst(t('form:secondLastIncomeTaxReturn'))}
                    type="proof_of_income"
                    documentUploaded={secondLastIncomeTaxReturn}
                    ownerId={guarantorId}
                    isGuarantor
                    description={
                      GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_INCOME_TAX_RETURN
                    }
                  />
                </>
              )}
              {(guarantorOccupation === USERS_TYPES.ITALIAN_STUDENT ||
                guarantorOccupation === USERS_TYPES.EMPLOYEE) && (
                <>
                  <DocumentField
                    documentSide={DOCUMENTS_SIDES.FRONT}
                    title={upperFirst(t('form:workContract'))}
                    type="proof_of_income"
                    documentUploaded={workContract}
                    ownerId={guarantorId}
                    isGuarantor
                    description={GUARANTOR_DOCS_DESCRIPTIONS.WORK_CONTRACT}
                  />
                  <DocumentField
                    documentSide={DOCUMENTS_SIDES.FRONT}
                    title={upperFirst(t('form:lastPaycheck'))}
                    type="proof_of_income"
                    documentUploaded={lastPaycheck}
                    ownerId={guarantorId}
                    isGuarantor
                    description={GUARANTOR_DOCS_DESCRIPTIONS.LAST_PAYCHECK}
                  />

                  <DocumentField
                    documentSide={DOCUMENTS_SIDES.FRONT}
                    title={upperFirst(t('form:secondLastPaycheck'))}
                    type="proof_of_income"
                    documentUploaded={secondLastPaycheck}
                    ownerId={guarantorId}
                    isGuarantor
                    description={
                      GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_PAYCHECK
                    }
                  />
                  <DocumentField
                    documentSide={DOCUMENTS_SIDES.FRONT}
                    title={upperFirst(t('form:thirdLastPaycheck'))}
                    type="proof_of_income"
                    documentUploaded={thirdLastPaycheck}
                    ownerId={guarantorId}
                    isGuarantor
                    description={
                      GUARANTOR_DOCS_DESCRIPTIONS.THIRD_LAST_PAYCHECK
                    }
                  />
                  {!state.isUserTheGuarantor && (
                    <>
                      <DocumentField
                        documentSide={DOCUMENTS_SIDES.FRONT}
                        title={`${upperFirst(t('identityDocument'))} (${t(
                          'front',
                        )})`}
                        type="identity_document"
                        documentUploaded={identityDocument}
                        ownerId={guarantorId}
                        isGuarantor
                      />
                      <DocumentField
                        documentSide={DOCUMENTS_SIDES.REAR}
                        title={`${upperFirst(t('identityDocument'))} (${t(
                          'rear',
                        )})`}
                        type="identity_document"
                        documentUploaded={identityDocument}
                        ownerId={guarantorId}
                        isGuarantor
                      />
                      <DocumentField
                        title={upperFirst(t('fiscalCode'))}
                        type="tax_number"
                        documentUploaded={taxNumber}
                        ownerId={guarantorId}
                        isGuarantor
                      />
                    </>
                  )}
                </>
              )}
            </StyledNarrowerContainer>
          </StyledNarrowWrapper>
          <UploadDocumentsActionBar
            isContinueButtonDisabled={!areAllDocumentsUploaded}
            onContinue={() => {
              navigate(
                `/${locale}/${t('routes:uploadDocuments')}/${
                  UPLOAD_DOCUMENTS_FLOW_STEPS.ADD_ANOTHER_GUARANTOR_OR_FINISH
                }?booking_id=${bookingId}`,
              );
            }}
          />
        </>
      )}
    </>
  );
};

export default UploadGuarantorDocumentsSteps;
